import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Download, Eye, EyeOff, Copy, Check, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import * as api from '../../api/educentialApi';

const PurchasedCards = () => {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visiblePins, setVisiblePins] = useState({});
  const [copiedPins, setCopiedPins] = useState({});

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const response = await api.getMyPurchases();
      setPurchases(response.data || response || []);
    } catch (err) {
      console.error('Error fetching purchases:', err);
    } finally {
      setLoading(false);
    }
  };

  const togglePinVisibility = (pinId) => {
    setVisiblePins(prev => ({
      ...prev,
      [pinId]: !prev[pinId]
    }));
  };

  const copyToClipboard = async (text, pinId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPins(prev => ({ ...prev, [pinId]: true }));
      setTimeout(() => {
        setCopiedPins(prev => ({ ...prev, [pinId]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      COMPLETED: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: CheckCircle,
        label: 'Completed'
      },
      PENDING: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        icon: Clock,
        label: 'Pending'
      },
      PROCESSING: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        icon: Clock,
        label: 'Processing'
      },
      FAILED: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        icon: AlertCircle,
        label: 'Failed'
      },
      REFUNDED: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        icon: AlertCircle,
        label: 'Refunded'
      }
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-4 w-4 mr-1" />
        {config.label}
      </span>
    );
  };

  const getPinStatusBadge = (status) => {
    const statusConfig = {
      UNUSED: {
        bg: 'bg-green-50',
        text: 'text-green-700',
        border: 'border-green-200',
        label: 'Available'
      },
      USED: {
        bg: 'bg-gray-50',
        text: 'text-gray-700',
        border: 'border-gray-200',
        label: 'Used'
      },
      EXPIRED: {
        bg: 'bg-red-50',
        text: 'text-red-700',
        border: 'border-red-200',
        label: 'Expired'
      }
    };

    const config = statusConfig[status] || statusConfig.UNUSED;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text} border ${config.border}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout title="My Scratch Cards">
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Scratch Cards">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">My Scratch Cards</h2>
              <p className="text-gray-600 mt-1">View and manage your purchased exam result checker cards</p>
            </div>
            <button
              onClick={() => navigate('/scratch-cards')}
              className="flex items-center px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors"
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              Buy More Cards
            </button>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm text-blue-600 font-medium">Total Purchases</p>
              <p className="text-3xl font-bold text-blue-900 mt-1">{purchases.length}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <p className="text-sm text-green-600 font-medium">Available PINs</p>
              <p className="text-3xl font-bold text-green-900 mt-1">
                {purchases.reduce((sum, p) => sum + (p.pins?.filter(pin => pin.status === 'UNUSED').length || 0), 0)}
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <p className="text-sm text-purple-600 font-medium">Total Spent</p>
              <p className="text-3xl font-bold text-purple-900 mt-1">
                ₦{purchases.reduce((sum, p) => sum + Number(p.totalAmount || 0), 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Purchases List */}
        {purchases.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <ShoppingCart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No purchases yet</h3>
            <p className="text-gray-600 mb-6">Start by purchasing your first exam result checker card</p>
            <button
              onClick={() => navigate('/scratch-cards')}
              className="inline-flex items-center px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors"
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              Browse Cards
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {purchases.map((purchase) => (
              <div key={purchase.id} className="bg-white rounded-lg shadow overflow-hidden">
                {/* Purchase Header */}
                <div className="bg-gray-50 border-b border-gray-200 p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">
                          {purchase.Product?.productName || 'Scratch Card'}
                        </h3>
                        {getStatusBadge(purchase.paymentStatus)}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Exam Body:</span>
                          <p className="text-gray-900">{purchase.Product?.examBody || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="font-medium">Quantity:</span>
                          <p className="text-gray-900">{purchase.quantity} card{purchase.quantity > 1 ? 's' : ''}</p>
                        </div>
                        <div>
                          <span className="font-medium">Amount Paid:</span>
                          <p className="text-gray-900 font-semibold">₦{Number(purchase.totalAmount).toLocaleString()}</p>
                        </div>
                        <div>
                          <span className="font-medium">Purchase Date:</span>
                          <p className="text-gray-900">{new Date(purchase.purchasedAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                      {purchase.paymentReference && (
                        <div className="mt-2 text-xs text-gray-500">
                          Reference: {purchase.paymentReference}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* PINs Section */}
                {purchase.paymentStatus === 'COMPLETED' && purchase.pins && purchase.pins.length > 0 ? (
                  <div className="p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      Your PINs ({purchase.pins.length})
                    </h4>
                    <div className="space-y-3">
                      {purchase.pins.map((pin) => (
                        <div
                          key={pin.id}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-yellow-300 transition-colors"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">PIN:</span>
                                <code className="px-3 py-1 bg-white border border-gray-300 rounded font-mono text-lg font-bold text-gray-900">
                                  {visiblePins[pin.id] ? pin.pin : '••••••••••••'}
                                </code>
                              </div>
                              <button
                                onClick={() => togglePinVisibility(pin.id)}
                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                                title={visiblePins[pin.id] ? 'Hide PIN' : 'Show PIN'}
                              >
                                {visiblePins[pin.id] ? (
                                  <EyeOff className="h-5 w-5 text-gray-600" />
                                ) : (
                                  <Eye className="h-5 w-5 text-gray-600" />
                                )}
                              </button>
                              <button
                                onClick={() => copyToClipboard(pin.pin, pin.id)}
                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                                title="Copy PIN"
                              >
                                {copiedPins[pin.id] ? (
                                  <Check className="h-5 w-5 text-green-600" />
                                ) : (
                                  <Copy className="h-5 w-5 text-gray-600" />
                                )}
                              </button>
                            </div>
                            {pin.serialNumber && (
                              <div className="text-sm text-gray-600">
                                Serial: <span className="font-mono">{pin.serialNumber}</span>
                              </div>
                            )}
                            {pin.expiresAt && (
                              <div className="text-sm text-gray-600">
                                Expires: {new Date(pin.expiresAt).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                          <div>
                            {getPinStatusBadge(pin.status)}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Instructions */}
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h5 className="font-semibold text-blue-900 mb-2 flex items-center">
                        <AlertCircle className="h-5 w-5 mr-2" />
                        How to Use Your PIN
                      </h5>
                      <ol className="text-sm text-blue-800 space-y-1 ml-7 list-decimal">
                        <li>Visit the official {purchase.Product?.examBody} result checking portal</li>
                        <li>Enter your examination number/registration number</li>
                        <li>Enter your PIN from above</li>
                        <li>Click "Check Result" to view your examination results</li>
                      </ol>
                    </div>
                  </div>
                ) : purchase.paymentStatus === 'PENDING' || purchase.paymentStatus === 'PROCESSING' ? (
                  <div className="p-6">
                    <div className="flex items-center justify-center p-8 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <Clock className="h-6 w-6 text-yellow-600 mr-3" />
                      <div>
                        <p className="font-semibold text-yellow-900">Payment {purchase.paymentStatus.toLowerCase()}</p>
                        <p className="text-sm text-yellow-700">Your PINs will be available once payment is confirmed</p>
                      </div>
                    </div>
                  </div>
                ) : purchase.paymentStatus === 'FAILED' ? (
                  <div className="p-6">
                    <div className="flex items-center justify-center p-8 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
                      <div>
                        <p className="font-semibold text-red-900">Payment failed</p>
                        <p className="text-sm text-red-700">Please try purchasing again or contact support</p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PurchasedCards;
